import React, { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Edit2, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ImageUpload } from "@/components/ImageUpload";

const langs = [
  { k: "en", label: "English" },
  { k: "hi", label: "हिंदी" },
  { k: "gu", label: "ગુજરાતી" },
];

const emptyItem = {
  category_id: "", name: { en: "", hi: "", gu: "" },
  description: { en: "", hi: "", gu: "" },
  price: 0, image_url: "", available: true, order: 0,
};

export const RestaurantMenu = () => {
  const { restaurant } = useAuth();
  const suspended = restaurant?.suspended;
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);

  const [catModal, setCatModal] = useState(null); // {name, order, id?}
  const [itemModal, setItemModal] = useState(null);

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/restaurant/categories"), api.get("/restaurant/items")]);
    setCats(c.data); setItems(i.data);
    if (!activeCat && c.data.length) setActiveCat(c.data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveCategory = async () => {
    if (!catModal.name) return;
    try {
      if (catModal.id) await api.patch(`/restaurant/categories/${catModal.id}`, { name: catModal.name, order: catModal.order || 0 });
      else await api.post("/restaurant/categories", { name: catModal.name, order: catModal.order || 0 });
      setCatModal(null); toast.success("Saved"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delCategory = async (c) => {
    if (!window.confirm("Delete category and all its items?")) return;
    await api.delete(`/restaurant/categories/${c.id}`);
    toast.success("Deleted"); load();
  };

  const openItemModal = (it) => {
    if (it) setItemModal({ ...it });
    else setItemModal({ ...emptyItem, category_id: activeCat || (cats[0]?.id ?? "") });
  };

  const saveItem = async () => {
    if (!itemModal.category_id) return toast.error("Pick a category");
    if (!itemModal.name?.en) return toast.error("English name required");
    try {
      const payload = { ...itemModal, price: Number(itemModal.price) || 0 };
      if (itemModal.id) await api.patch(`/restaurant/items/${itemModal.id}`, payload);
      else await api.post("/restaurant/items", payload);
      toast.success("Saved");
      setItemModal(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delItem = async (it) => {
    if (!window.confirm("Delete item?")) return;
    await api.delete(`/restaurant/items/${it.id}`);
    toast.success("Deleted"); load();
  };
  const toggleAvail = async (it) => {
    await api.post(`/restaurant/items/${it.id}/availability`);
    load();
  };

  const filtered = activeCat ? items.filter((i) => i.category_id === activeCat) : items;

  return (
    <div data-testid="rest-menu">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Menu</h1>
          <p className="text-sm text-zq-muted mt-1">Categories and items in 3 languages.</p>
        </div>
        <div className="flex gap-2">
          <button className="zq-btn zq-btn-outline" disabled={suspended} onClick={() => setCatModal({ name: "", order: cats.length })} data-testid="new-cat-btn"><Plus size={16}/> Category</button>
          <button className="zq-btn zq-btn-primary" disabled={suspended || cats.length === 0} onClick={() => openItemModal(null)} data-testid="new-item-btn"><Plus size={16}/> Item</button>
        </div>
      </div>

      {/* Categories */}
      {cats.length > 0 && (
        <div className="flex gap-2 mt-6 overflow-x-auto zq-scroll pb-2">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-1">
              <button
                className={`zq-pill whitespace-nowrap ${activeCat === c.id ? "zq-pill-orange" : "zq-pill-gray"}`}
                onClick={() => setActiveCat(c.id)}
                data-testid={`cat-tab-${c.id}`}
              >{c.name}</button>
              {!suspended && (
                <>
                  <button className="text-xs text-zq-muted" onClick={() => setCatModal({ ...c })}><Edit2 size={12}/></button>
                  <button className="text-xs text-red-600" onClick={() => delCategory(c)}><Trash2 size={12}/></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {cats.length === 0 && (
        <div className="zq-card p-8 mt-6 text-center text-zq-muted">
          Add your first category to start building the menu.
        </div>
      )}

      {/* Items */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {filtered.map((it) => (
          <div key={it.id} className="zq-card overflow-hidden">
            {it.image_url && <img src={it.image_url} alt={it.name?.en} className="h-36 w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{it.name?.en}</div>
                  <div className="text-xs text-zq-muted line-clamp-1">{it.description?.en}</div>
                </div>
                <button onClick={() => toggleAvail(it)} className="text-zq-orange" data-testid={`toggle-${it.id}`}>
                  {it.available ? <ToggleRight size={28} /> : <ToggleLeft size={28} className="text-gray-400"/>}
                </button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-bold">{inr(it.price)}</span>
                <span className="zq-pill zq-pill-gray text-[10px]">{it.available ? "Available" : "Out of stock"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button className="zq-btn zq-btn-outline flex-1 !py-1.5" onClick={() => openItemModal(it)} data-testid={`edit-item-${it.id}`}><Edit2 size={14}/> Edit</button>
                <button className="zq-btn zq-btn-ghost text-red-600" onClick={() => delItem(it)} data-testid={`delete-item-${it.id}`}><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {cats.length > 0 && filtered.length === 0 && (
          <div className="zq-card p-6 text-zq-muted col-span-full text-center">No items in this category yet.</div>
        )}
      </div>

      {/* Category modal */}
      {catModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="zq-card w-full max-w-sm p-6">
            <button className="absolute m-2 top-3 right-3" onClick={() => setCatModal(null)}><X size={18}/></button>
            <h3 className="text-lg font-bold">{catModal.id ? "Edit category" : "New category"}</h3>
            <input className="zq-input mt-4" placeholder="Name" value={catModal.name} onChange={(e) => setCatModal({ ...catModal, name: e.target.value })} data-testid="cat-name-input"/>
            <input className="zq-input mt-3" type="number" placeholder="Order" value={catModal.order || 0} onChange={(e) => setCatModal({ ...catModal, order: Number(e.target.value) })}/>
            <button className="zq-btn zq-btn-primary w-full mt-4" onClick={saveCategory} data-testid="cat-save-btn">Save</button>
          </div>
        </div>
      )}

      {/* Item modal */}
      {itemModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="zq-card w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
            <button className="absolute m-2 top-3 right-3" onClick={() => setItemModal(null)}><X size={18}/></button>
            <h3 className="text-lg font-bold">{itemModal.id ? "Edit item" : "New item"}</h3>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-xs font-medium text-zq-muted">Category</label>
                <select className="zq-input" value={itemModal.category_id} onChange={(e) => setItemModal({ ...itemModal, category_id: e.target.value })} data-testid="item-category">
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-zq-muted">Price (₹)</label>
                <input className="zq-input" type="number" value={itemModal.price} onChange={(e) => setItemModal({ ...itemModal, price: e.target.value })} data-testid="item-price"/>
              </div>
            </div>
            <div className="mt-3">
              <ImageUpload
                value={itemModal.image_url}
                onChange={(url) => setItemModal({ ...itemModal, image_url: url })}
                folder={`zaykaqr/restaurants/${restaurant?.id}/menu`}
                label="Item Image"
                aspect="aspect-video"
                testId="item-image-upload"
              />
            </div>

            <div className="mt-4 zq-card p-4">
              <h4 className="font-semibold text-sm">Translations</h4>
              {langs.map((lg) => (
                <div key={lg.k} className="mt-3">
                  <div className="text-xs font-medium text-zq-muted">{lg.label}</div>
                  <input className="zq-input mt-1" placeholder={`Name (${lg.label})`} value={itemModal.name?.[lg.k] || ""} onChange={(e) => setItemModal({ ...itemModal, name: { ...itemModal.name, [lg.k]: e.target.value } })} data-testid={`item-name-${lg.k}`}/>
                  <textarea className="zq-input mt-2" rows={2} placeholder={`Description (${lg.label})`} value={itemModal.description?.[lg.k] || ""} onChange={(e) => setItemModal({ ...itemModal, description: { ...itemModal.description, [lg.k]: e.target.value } })}/>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <input id="avail" type="checkbox" checked={itemModal.available} onChange={(e) => setItemModal({ ...itemModal, available: e.target.checked })}/>
              <label htmlFor="avail" className="text-sm">Available</label>
            </div>
            <button className="zq-btn zq-btn-primary w-full mt-4" onClick={saveItem} data-testid="item-save-btn">Save item</button>
          </div>
        </div>
      )}
    </div>
  );
};
